import { type ReactNode, useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface TableToolbarProps {
  /** Committed search term (the value the consumer queries with). */
  search: string;
  /** Called with the debounced search term. */
  onSearchChange: (search: string) => void;
  searchPlaceholder?: string;
  /** Debounce window in ms before `onSearchChange` fires. Defaults to 300. */
  debounceMs?: number;
  /** Accessible label for the search input. */
  searchAriaLabel?: string;
  /** Filter controls (status `<select>`, etc.) rendered to the right. */
  children?: ReactNode;
  className?: string;
}

/**
 * A list-screen toolbar: a debounced search input on the left, arbitrary
 * filter controls (`children`) on the right. The input is locally controlled
 * for snappy typing and only pushes to `onSearchChange` after `debounceMs`.
 * A clear button resets immediately (no debounce).
 */
export function TableToolbar({
  search,
  onSearchChange,
  searchPlaceholder = "Search...",
  debounceMs = 300,
  searchAriaLabel = "Search",
  children,
  className,
}: TableToolbarProps) {
  const [value, setValue] = useState(search);
  // Track the last value we emitted so an external `search` reset (e.g. a
  // "clear filters" button) re-syncs the local input without re-emitting.
  const lastEmitted = useRef(search);

  useEffect(() => {
    if (search !== lastEmitted.current) {
      lastEmitted.current = search;
      setValue(search);
    }
  }, [search]);

  useEffect(() => {
    if (value === lastEmitted.current) return;
    const id = setTimeout(() => {
      lastEmitted.current = value;
      onSearchChange(value);
    }, debounceMs);
    return () => clearTimeout(id);
  }, [value, debounceMs, onSearchChange]);

  const clear = () => {
    setValue("");
    lastEmitted.current = "";
    onSearchChange("");
  };

  return (
    <div
      className={cn(
        "mb-4 flex flex-wrap items-center justify-between gap-3",
        className
      )}
    >
      <div className="relative w-full sm:w-56">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          aria-label={searchAriaLabel}
          placeholder={searchPlaceholder}
          className="pl-9 pr-11"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="icon-touch"
            aria-label="Clear search"
            onClick={clear}
            className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {children && (
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          {children}
        </div>
      )}
    </div>
  );
}
